import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { firestoreCacheMode } from './firebase'
import { idbDiagnosticTags, pendingOpenMs } from './idbDiagnostics'
import { log } from './logger'
import { bootMilestones } from './perf'
import { isAbortError, isIndexedDbBreakage, isSideDatabaseClosing } from './idbBreakage'
import { Sentry } from './sentry'
import { consumeBootKind } from './webViewRestart'

// Context for chasing Firestore's IndexedDB failures (#459): how this session
// started, how long since boot / the last return from background, which OTA
// bundle is really running, and how full storage is. Attached to every
// GlitchTip event as tags so they can be filtered and compared. Also which
// IndexedDB databases failed, and — 15 s after the first IndexedDB breakage of
// a session — a follow-up message saying whether the page recovered (#536).
const bootAt = Date.now()
const bootKind = consumeBootKind()
let lastResumeAt: number | null = null
let lastBackgroundAt: number | null = null
let lastBackgroundMs: number | null = null
let bundle: { id: string; version: string } | null = null
let idbFollowUpScheduled = false
let abortFollowUpScheduled = false

// The breakage event is already sent by the time we'd know whether data still
// arrived, so a separate message carries the outcome.
function sendFollowUp(message: string, fingerprint: string) {
  const m = bootMilestones()
  Sentry.captureMessage(message, {
    level: 'info',
    fingerprint: [fingerprint],
    tags: {
      fs_first_snapshot: String(m.firstSnapshot),
      auth_resolved: String(m.authResolved),
      page_visible: String(document.visibilityState === 'visible'),
    },
  })
  log('idb', 'follow-up sent', { fingerprint, ...m })
}

// Firestore's IndexedDB open can hang without ever erroring (#611), leaving the
// page with no data and nothing reported. Says whether that page was dead, to
// decide whether a hung open needs its own recovery.
const FIRESTORE_OPEN_CHECK_MS = 15_000

// A page hidden or backgrounded before the check has its timers and IndexedDB
// suspended, so a pending open there says nothing about a dead page. iOS
// WKWebView can stay `visible` while the app is backgrounded, so the app state
// and a timer that ran late (the page was frozen) count too. Check again once
// the page has been active for the same window.
const TIMER_LATE_MS = 5_000
let appActive = true
let onNextActive: (() => void) | null = null

function scheduleFirestoreOpenCheck(deferred: boolean) {
  const dueAt = Date.now() + FIRESTORE_OPEN_CHECK_MS
  setTimeout(() => checkFirestoreOpen(dueAt, deferred), FIRESTORE_OPEN_CHECK_MS)
}

function checkFirestoreOpen(dueAt: number, deferred: boolean) {
  if (firestoreCacheMode !== 'persistent') return
  const pendingMs = pendingOpenMs('firestore/')
  if (pendingMs == null) return
  const lateMs = Date.now() - dueAt
  const active = appActive && document.visibilityState === 'visible'
  if (!active || lateMs > TIMER_LATE_MS) {
    if (deferred) return
    log('idb', 'firestore open pending while suspended — deferring check', { pendingMs, lateMs, appActive })
    if (active) scheduleFirestoreOpenCheck(true)
    else onNextActive = () => scheduleFirestoreOpenCheck(true)
    return
  }
  const m = bootMilestones()
  Sentry.captureMessage('firestore-idb-open-pending', {
    level: 'warning',
    fingerprint: ['firestore-idb-open-pending'],
    tags: {
      idb_open_pending_ms: String(pendingMs),
      fs_first_snapshot: String(m.firstSnapshot),
      auth_resolved: String(m.authResolved),
      page_visible: String(document.visibilityState === 'visible'),
      deferred_while_hidden: String(deferred),
      check_late_ms: String(lateMs),
    },
  })
  log('idb', 'firestore open still pending', { pendingMs, ...m })
}

function onActiveChange(isActive: boolean) {
  const now = Date.now()
  appActive = isActive
  if (isActive) {
    const resume = onNextActive
    onNextActive = null
    resume?.()
    lastResumeAt = now
    lastBackgroundMs = lastBackgroundAt ? now - lastBackgroundAt : null
    log('lifecycle', 'foreground', { backgroundMs: lastBackgroundMs })
  } else {
    lastBackgroundAt = now
    log('lifecycle', 'background', { msSinceBoot: now - bootAt })
  }
}

async function storageEstimate(): Promise<Record<string, string>> {
  try {
    const { usage, quota } = await navigator.storage.estimate()
    if (usage == null || quota == null) return {}
    return { storage_usage_mb: String(Math.round(usage / 1e6)), storage_quota_mb: String(Math.round(quota / 1e6)) }
  } catch {
    return {}
  }
}

export function installSessionDiagnostics() {
  log('lifecycle', 'boot', { bootKind })
  scheduleFirestoreOpenCheck(false)

  if (Capacitor.isNativePlatform()) {
    void App.addListener('appStateChange', ({ isActive }) => onActiveChange(isActive))
    void CapacitorUpdater.current()
      .then(({ bundle: b }) => { bundle = { id: b.id, version: b.version } })
      .catch(() => {})
  } else {
    document.addEventListener('visibilitychange', () => onActiveChange(document.visibilityState === 'visible'))
  }

  Sentry.addEventProcessor(async event => {
    const now = Date.now()
    event.tags = {
      ...event.tags,
      boot_kind: bootKind,
      ms_since_boot: String(now - bootAt),
      ms_since_resume: lastResumeAt ? String(now - lastResumeAt) : 'never',
      last_background_ms: lastBackgroundMs != null ? String(lastBackgroundMs) : 'never',
      ...(bundle && { bundle_id: bundle.id, bundle_version: bundle.version }),
      ...(await storageEstimate()),
      fs_cache: firestoreCacheMode,
      ...idbDiagnosticTags(),
    }
    if (event.exception && !idbFollowUpScheduled && isIndexedDbBreakage(event) && !isSideDatabaseClosing(event)) {
      idbFollowUpScheduled = true
      setTimeout(() => sendFollowUp('indexeddb-broken follow-up', 'indexeddb-broken-followup'), 15_000)
    } else if (event.exception && !abortFollowUpScheduled && isAbortError(event) && !isIndexedDbBreakage(event)) {
      // A bare, stackless AbortError with no IndexedDB trouble (GlitchTip
      // 8732240): says whether the page it came from still got its data.
      abortFollowUpScheduled = true
      setTimeout(() => sendFollowUp('bare-abort-error follow-up', 'bare-abort-error-followup'), 15_000)
    }
    return event
  })
}
